import { RouteFactory } from "@app/routePaths";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import GAData from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import MetaTags from "@components/MetaTags/MetaTags";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { useDelayedComponents } from "@hooks/useDelayedComponents";
import AppBanner from "@pages/Article/components/AppBanner";
import SectionHeading from "@pages/Section/components/SectionHeading";
import PodcastsFeaturedStories from "@pages/Section/layouts/PodcastsPage/components/PodcastsFeaturedStories";
import PodcastsKeywordsSectionStories from "@pages/Section/layouts/PodcastsPage/components/PodcastsKeywordsSectionStories/PodcastsSectionStories";
import PodcastsPromoteBannerVersion1 from "@pages/Section/layouts/PodcastsPage/components/PodcastsPromoteBannerVersion1";
import PodcastsPromoteBannerVersion2 from "@pages/Section/layouts/PodcastsPage/components/PodcastsPromoteBannerVersion2";
import {
  BT_CORRESPONDENTS_DETAILS,
  BT_PODCASTS_DETAILS,
  LENS_ON_DETAILS,
  MARKET_FOCUS_DETAILS,
  MONEY_HACKS_DETAILS,
} from "@pages/Section/layouts/PodcastsPage/utils/constants";
import {
  PodcastKeywordArticleMapping,
  PodcastPagePayload,
} from "@pages/Section/layouts/PodcastsPage/utils/types";
import { cn, getStaticSectionJSONLD } from "@util/helpers";
import { useDetectAdBlock } from "adblock-detect-react";

type PodcastPageContentProps = PodcastPagePayload["data"];

/**
 * Component to render the content of the Podcasts page.
 * Figma: https://www.figma.com/design/xsbEGttNPBTNUi388qtSJD/PODCASTS--WEB-?node-id=3702-361227&t=frjsk7DkSOdPUpFL-4
 */
const PodcastPageContent = ({
  title,
  podcastsFeaturedArticles,
  lensOnArticles,
  moneyHacksArticles,
  marketFocusArticles,
  btCorrespondentsArticles,
  btPodcastsArticles,
  seoFooterData,
}: PodcastPageContentProps): React.ReactElement => {
  const { isShown } = useDelayedComponents(800);
  const adBlockDetected = useDetectAdBlock();

  const termName = "Podcasts";
  const articleMapping: PodcastKeywordArticleMapping[] = [
    {
      name: "money_hacks",
      articles: moneyHacksArticles,
      details: MONEY_HACKS_DETAILS,
    },
    { name: "lens_on", articles: lensOnArticles, details: LENS_ON_DETAILS },
    {
      name: "bt_podcasts",
      articles: btPodcastsArticles,
      details: BT_PODCASTS_DETAILS,
    },
    {
      name: "bt_correspondents",
      articles: btCorrespondentsArticles,
      details: BT_CORRESPONDENTS_DETAILS,
    },
    {
      name: "market_focus",
      articles: marketFocusArticles,
      details: MARKET_FOCUS_DETAILS,
    },
  ];

  return (
    <div className="w-full" data-testid="podcasts-page-content-component">
      <AdSettings
        adNames={["lb1", "imu1", "imu2", "prestitial", "catfish"]}
        path={RouteFactory.podcasts}
        adTargets={[{ key: "page", value: "listing" }]}
      />

      <MetaTags
        title={title}
        description={`THE BUSINESS TIMES ${termName} - Find ${termName} News & Headlines, insight and analysis in Singapore, Asia-Pacific & global markets news at The Business Times.`}
        ogType="article"
        slug={RouteFactory.podcasts}
        jsonLd={getStaticSectionJSONLD("Podcasts", RouteFactory.podcasts)}
      />

      <GAData
        title="Podcasts"
        level2="podcasts"
        adblocker={adBlockDetected ? 1 : 0}
      />

      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header />

      <PulseEntry />

      <Container></Container>

      <Container rootClassName="grid grid-cols-1 lg:grid-cols-3">
        <SectionHeading
          text={"Podcasts"}
          className={"cols-span-1 uppercase lg:col-span-3"}
          hasBorder
        />

        <div className="col-span-1 lg:col-span-2 lg:mb-6 lg:mr-6">
          <PodcastsFeaturedStories
            className="cols-span-2 pt-2"
            cardProps={podcastsFeaturedArticles}
          />
        </div>

        <div className="mt-6 h-full lg:row-span-2">
          <PodcastsPromoteBannerVersion1 />

          <Ad
            adType="imu1"
            className="mb-10 flex flex-col items-center justify-center self-start bg-gray-150 py-4 lg:sticky lg:lg:top-[70px] lg:pt-6"
          />
        </div>

        <div className="col-span-1 lg:col-span-2 lg:mr-6">
          {articleMapping.map((mapping, index) => {
            return (
              <>
                <PodcastsKeywordsSectionStories
                  cardProps={mapping.articles}
                  details={mapping.details}
                  key={mapping.name}
                  className={cn({ "mb-0": index === 4 || index === 1 })}
                />

                {index === 1 || index === 4 ? (
                  <PodcastsPromoteBannerVersion2 />
                ) : null}
              </>
            );
          })}
        </div>
      </Container>

      <SeoFooter data={seoFooterData} />

      {isShown ? <AppBanner /> : null}

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default PodcastPageContent;
